import { isBannerCategory } from '../util/banners';

export default class LearnerModel {
  constructor(learnerInfo) {
    if (!learnerInfo) {
      // eslint-disable-next-line no-console
      console.info('Learner information is required to create a LearnerModel instance.');

      return;
    }

    this._describesYourCollegeExperience = learnerInfo.describesYourCollegeExperience;
    this._asuRite = learnerInfo.asuRite;
    this._benefitType = learnerInfo.benefitType;
    this._sub = learnerInfo.sub;
    this._openEdxId = learnerInfo.openEdxId;
    this._partnerId = learnerInfo.partnerId;
    this._partnerName = learnerInfo.partnerName;
    this._isVoucherAllowed = learnerInfo.isVoucherAllowed === true;
    this._firstName = learnerInfo.firstName;
    this._lastName = learnerInfo.lastName;
    this._preferredName = learnerInfo.preferredName;
    this._preferredFirstName = learnerInfo.preferredFirstName;
    this._emailId = learnerInfo.emailId;
    this._emailotpCustomOauthId = learnerInfo.emailotpCustomOauthId || learnerInfo.emailotp_custom_oauth_id;
    this._permissionToText = learnerInfo.permissionToText;
    this._phoneNumber = learnerInfo.phoneNumber;
    this._birthDate = learnerInfo.birthDate ? learnerInfo.birthDate : '';
    this._learnerId = learnerInfo.learnerId;
    this._idVerify = learnerInfo.idVerify;
    this._idVerifyDate = learnerInfo.idVerifyDate;
    this._idVerificationDeniedMessage = learnerInfo.idVerificationDeniedMessage;
    this._idVerificationPortalExpiration = learnerInfo.idVerificationPortalExpiration;
    const normalizedStudentAnnouncements = (learnerInfo.studentAnnouncements || []).map((a) => {
      if (typeof a === 'string') {
        return { id: null, name: null, body: a, createdDate: null, category: 'announcement' };
      }
      return a;
    });
    // Banner-category announcements render as banners at the top of the app, not
    // in the announcement / notification lists, so split them out at the source
    // (every consumer of _studentAnnouncements then excludes banners for free).
    this._studentAnnouncements = normalizedStudentAnnouncements.filter((a) => !isBannerCategory(a && a.category));
    this._bannerAnnouncements = normalizedStudentAnnouncements.filter((a) => isBannerCategory(a && a.category));
    this._partnerAnnouncements = learnerInfo.partnerAnnouncements;
    this._isAdmin = learnerInfo.isAdmin;
    this._viewAs = learnerInfo.viewAs;
    this._ethnicity = learnerInfo.ethnicity;
    this._gender = learnerInfo.gender;
    this._needsEnrollmentSupport = learnerInfo.needsEnrollmentSupport;
    this._householdIncome = learnerInfo.householdIncome;
    this._eligibilityStartDate = learnerInfo.eligibilityStartDate;
    this._createdDate = learnerInfo.createdDate;
    this._eligibilityEndDate = learnerInfo.eligibilityEndDate;
    this._seperationDate = learnerInfo.seperationDate;
    this._eligibilityStatus = learnerInfo.eligibilityStatus;
    this._emailAlt = learnerInfo.emailAlt;
    this._eaOptIn = learnerInfo.eaOptIn === 'true';
    this._eaOptInDate = learnerInfo.eaOptInDate;
    this._collectDemographicInfo = learnerInfo.collectDemographicInfo;
    this._numberOfActiveEnrollments = learnerInfo.numberOfActiveEnrollments;
    this._numberOfCreditsNeeded = learnerInfo.numberOfCreditsNeeded;
    this._currentGPA = learnerInfo.currentGPA;
    this._generalAdmissionMinGPA = learnerInfo.generalAdmissionMinGPA;
    /*this._mailingAddress = {
            "street" : <string>,
            "city" : <string>,
            "state" : <string>,
            "postalCode" : <string>,
            "country" : <string>
        };*/
    this._mailingAddress = learnerInfo.mailingAddress || {};
    this.pathwayId = learnerInfo.pathwayId;
    this.pathwayName = learnerInfo.pathwayName;
    this.recommendedPathwayId = learnerInfo.recommendedPathwayId; // Recommended pathway (e.g., most popular) - legacy single pathway
    this.recommendedPathwayIds = learnerInfo.recommendedPathwayIds || []; // Multiple recommended pathways (top 3)
    this.isPathwayConfettiFired = learnerInfo.isPathwayConfettiFired;
    this.pathwayCompletionDate = learnerInfo.pathwayCompletionDate;
    this._minPathwayGPA = learnerInfo.minGPA;
    this._numberOfCourseNeededForAdmission = learnerInfo.numberOfCourseNeededForAdmission;
    this._maxActiveEnrollmentsAllowed = learnerInfo.maxActiveEnrollmentsAllowed;
    this._isFirstTimeUser = false;
    this._contactId = learnerInfo.contactId;
    this._contactSalesforceId = learnerInfo.contactSalesforceId;
    this._numberOfCreditsCompleted = learnerInfo.numberOfCreditsCompleted;
    this._currentGPA = parseFloat(Math.round(learnerInfo.currentGPA * 100) / 100).toFixed(2);
    this._partnerURLSubDomain = learnerInfo.partnerURLSubDomain;
    this._totalCoursesConverted = learnerInfo.totalCoursesConverted;
    this._celebrateEvent = learnerInfo.celebrateEvent;
    this._isEarnedAdmission = learnerInfo.isEarnedAdmission;
    this._canvasUserId = learnerInfo.canvasUserId;
    this._isPathwaysAllowed = learnerInfo.isPathwaysAllowed;
    this._isCertificatesAllowed = learnerInfo.isCertificatesAllowed;
    this._nonEnrollableCourseList = learnerInfo.nonEnrollableCourses;
    this._learnersIntent = learnerInfo.learnersIntent;
    this._pronoun = learnerInfo.pronoun;
    this._name = '';
    this._firstPortalSignIn = learnerInfo.firstPortalSignIn;
    this._applicationNotices = learnerInfo.applicationNotices;
    this._coachDetails = { ...learnerInfo.coachDetails };
    this._isCanvasAdmin = learnerInfo.isCanvasAdmin;
    this._referenceId = learnerInfo.reference_id;
    this._blockAccess = learnerInfo.blockAccess;
    this._blockAccessReason = learnerInfo.blockAccessReason;
    this._showModalForCourseAlternativeWhenODSelected = learnerInfo.showModalForCourseAlternativeWhenODSelected;
    this._allowAccessToAdmittedStudent = learnerInfo.allowAccessToAdmittedStudent;
    this._allowAccessToAdmittedStudentReason = learnerInfo.allowAccessToAdmittedStudentReason;
    this._disqualifyLearner = learnerInfo.disqualifyLearner;
    this._notificationsEmail = learnerInfo.notificationsEmail;
    this._p2aEaChecklistStatus = learnerInfo.p2aEaChecklistStatus;
    this._p2aEaOptInDate = learnerInfo.p2aEaOptInDate;
    this._partnerType = learnerInfo.partnerType;
    this._currentHighSchool = learnerInfo.currentHighSchool;
    this._highSchoolType = learnerInfo.highSchoolType;
    this._highSchoolExpectedGraduationDate = learnerInfo.highSchoolExpectedGraduationDate;
    this._courseOffering = learnerInfo.courseOffering;
    this._trackAADegree = learnerInfo.trackAADegree;
    this._useAetherFlow = learnerInfo.useAetherFlow || false;
  }

  get contactId() {
    return this._contactId;
  }

  get contactSalesforceId() {
    return this._contactSalesforceId;
  }

  get useAetherFlow() {
    return this._useAetherFlow;
  }

  get courseListPageData() {
    return {
      numberOfCourseNeededForAdmission: this._numberOfCourseNeededForAdmission,
      numberOfCreditsNeeded: this._numberOfCreditsNeeded,
      maxActiveEnrollmentsAllowed: this._maxActiveEnrollmentsAllowed,
      openEdxId: this._openEdxId,
      asuRite: this._asuRite,
      sub: this._sub,
      describesYourCollegeExperience: this._describesYourCollegeExperience
    };
  }

  get partnerULRSubdomainWithEnv() {
    if (import.meta.env.VITE_ENV_HOOK === 'dev' || import.meta.env.VITE_ENV_HOOK === 'localhost') {
      return `${this._partnerURLSubDomain}-dev`;
    } else if (import.meta.env.VITE_ENV_HOOK === 'qa') {
      return `${this._partnerURLSubDomain}-qa`;
    } else if (import.meta.env.VITE_ENV_HOOK === 'staging') {
      return `${this._partnerURLSubDomain}-staging`;
    } else {
      return this._partnerURLSubDomain;
    }
  }

  get learnerId() {
    return this._learnerId;
  }

  get partnerId() {
    return this._partnerId;
  }

  get isVoucherAllowed() {
    return this._isVoucherAllowed;
  }

  get collectDemographicInfo() {
    return this._collectDemographicInfo && this._collectDemographicInfo.toLowerCase() === 'true';
  }

  set basicLearnerInformation(learnerInfo) {
    this._phoneNumber = learnerInfo.phoneNumber;
    this._birthDate = learnerInfo.birthDate;
    this._mailingAddress = learnerInfo.mailingAddress;
    this._gender = learnerInfo.gender;
    this._ethnicity = learnerInfo.ethnicity;
    this._householdIncome = learnerInfo.householdIncome;
  }

  get basicLearnerInformation() {
    return {
      firstName: this._firstName,
      lastName: this._lastName,
      email: this._emailId,
      phoneNumber: this._phoneNumber,
      eaOptIn: this._eaOptIn,
      birthDate: this._birthDate,
      mailingAddress: this._mailingAddress,
      gender: this._gender,
      ethnicity: this._ethnicity,
      householdIncome: this._householdIncome,
      partnerId: this._partnerId,
      contactId: this._contactId,
      pronoun: this._pronoun,
      learnersIntent: this._learnersIntent,
      eligibilityStartDate: this._eligibilityStartDate ? this.formatDate(this._eligibilityStartDate) : '',
      eligibilityEndDate: this._eligibilityEndDate ? this.formatDate(this._eligibilityEndDate) : '',
      preferredFirstName: this._preferredFirstName || this._firstName,
      // OS-4026 Request to Make "Preferred Name: Last Name" field pull from "Primary Name: Last Name" in SF
      preferedLastName: this._lastName,
      notificationsEmail: this._notificationsEmail
    };
  }

  get learnerPreferedName() {
    return {
      preferredFirstName: this._preferredFirstName,
      // OS-4026 Request to Make "Preferred Name: Last Name" field pull from "Primary Name: Last Name" in SF
      preferedLastName: this._lastName
    };
  }

  get demographicInformation() {
    return {
      gender: this._gender,
      birthDate: this._birthDate,
      ethnicity: this._ethnicity,
      householdIncome: this._householdIncome,
      pronoun: this._pronoun,
      learnersIntent: this._learnersIntent
    };
  }

  get basicLearnerInfo() {
    return {
      name: this._firstName + ' ' + this._lastName,
      emailId: this._emailId,
      birthDate: this._birthDate,
      mailingAddress: this._mailingAddress,
      phoneNumber: this._phoneNumber,
      notificationsEmail: this._notificationsEmail
    };
  }

  get educationalInformation() {
    return {
      currentHighSchool: this._currentHighSchool,
      highSchoolType: this._highSchoolType,
      highSchoolExpectedGraduationDate: this._highSchoolExpectedGraduationDate
    };
  }

  get eaOptIn() {
    return {
      eaOptIn: true
    };
  }

  set eaOptIn(value) {
    this._eaOptIn = value;
  }

  get dashboardPageData() {
    return {
      firstName: this._firstName,
      lastName: this._lastName,
      eligibilityStatus: this._eligibilityStatus && this._eligibilityStatus.toLowerCase() === 'true',
      asuRite: this._asuRite,
      isFirstTimeUser: this._isFirstTimeUser,
      numberOfCourseNeededForAdmission: this._numberOfCourseNeededForAdmission,
      numberOfCreditsNeeded: this._numberOfCreditsNeeded,
      numberOfCreditsCompleted: this._numberOfCreditsCompleted,
      currentGPA: this._currentGPA
    };
  }

  get openEdxRegisterData() {
    return {
      email: this._emailId,
      username: this._asuRite,
      first_name: this._firstName,
      last_name: this._lastName
    };
  }

  set openEdxId(openEdxId) {
    this._openEdxId = openEdxId;
  }

  get openEdxId() {
    return this._openEdxId;
  }

  get birthDate() {
    return this._birthDate;
  }

  get emailotpCustomOauthId() {
    return this._emailotpCustomOauthId;
  }

  set sub(subPayload) {
    this._sub = subPayload;
  }

  set describesYourCollegeExperience(exp) {
    this._describesYourCollegeExperience = exp;
  }

  set isFirstTimeUser(val) {
    this._isFirstTimeUser = val;
  }

  formatDate(str) {
    return [str.split('/')[2], str.split('/')[0], str.split('/')[1]].join('-');
  }

  get pathwayCourseListPageData() {
    return {
      openEdxId: this._openEdxId,
      asuRite: this._asuRite,
      firstName: this._firstName,
      lastName: this._lastName,
      eligibilityStatus: this._eligibilityStatus && this._eligibilityStatus.toLowerCase() === 'true',
      numberOfCreditsNeeded: this._numberOfCreditsNeeded,
      numberOfCreditsCompleted: this._numberOfCreditsCompleted
    };
  }

  get courseOffering() {
    return this._courseOffering;
  }
}
